import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { EntityColumn } from './types';
import i18n from '../../i18n';
import {
  DATA_OPS, FilterType, NO_VALUE_TOKEN, OPERATORS, PARSEABLE_ONLY_OPS, booleanLookupOptions, currentOp, filterPrefix, isPresetOp,
} from './columnFilterOps';

// Unified list-filters program (spec 2026-07-22) — compact read-only summary of active
// `filter.<col>.<op>` overrides with a one-click clear per chip. Rewritten from CRM's Tailwind
// source into Fluent `makeStyles` for supervision. Discoverability companion to the header-driven
// filter popovers (ColumnHeaderFilter).

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalM,
  },
});

export interface ActiveFiltersChipsProps<TItem> {
  /** Column catalog, for labels + filterType (to resolve the operator's display label). */
  columns: EntityColumn<TItem>[];
  /** The flat filter record — one condition per column (v1). */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

/** `filter.<col>.<op>` — col may itself contain dots; the LAST segment is the op. */
const FILTER_KEY_PATTERN = /^filter\.(.+)\.([^.]+)$/;

interface Chip {
  key: string;
  label: string;
  /** Every filter key this chip stands for — a date preset is ONE chip over TWO keys. */
  keys: string[];
}

export function ActiveFiltersChips<TItem>({ columns, value, onChange }: ActiveFiltersChipsProps<TItem>) {
  const styles = useStyles();
  // A date preset (contract v2 D1) is a gte/lt TOKEN pair; it reads as one chip — "Due: This week" —
  // never as two chips of raw {{tokens}}, and removing it removes both keys.
  const presetChips: Chip[] = [];
  const presetKeys = new Set<string>();
  for (const col of columns) {
    if (col.filterType !== 'date-range') continue;
    const op = currentOp(col, value);
    if (!isPresetOp(op)) continue;
    const keys = [`${filterPrefix(col.key)}gte`, `${filterPrefix(col.key)}lt`];
    keys.forEach((k) => presetKeys.add(k));
    const label = OPERATORS['date-range'].find((o) => o.op === op)?.label ?? op;
    presetChips.push({ key: keys[0], keys, label: `${col.label}: ${label}` });
  }

  const chips: Chip[] = presetChips.concat(Object.entries(value).flatMap(([key, v]) => {
    if (presetKeys.has(key)) return [];
    const m = FILTER_KEY_PATTERN.exec(key);
    if (!m) return [];
    const [, colKey, op] = m;
    const col = columns.find((c) => c.key === colKey);
    const colLabel = col?.label ?? colKey;
    const ops = col?.filterType
      ? [...OPERATORS[col.filterType as FilterType], ...(PARSEABLE_ONLY_OPS[col.filterType as FilterType] ?? [])]
      : undefined;
    const opLabel = ops?.find((o) => o.op === op)?.label ?? op;
    // For lookup values, show the option labels rather than raw codes; C86's "(has no value)" token reads as its words,
    // and a boolean's codes as Yes / No.
    const lookupOptions = col?.filterType === 'boolean' ? booleanLookupOptions() : col?.filterOptions;
    const displayValue = DATA_OPS.has(op) || v === ''
      ? ''
      : v.split(',').map((s) => s === NO_VALUE_TOKEN
        ? i18n.t('filters.lookup.hasNoValue')
        : lookupOptions?.find((o) => o.value === s)?.label ?? s).join(', ');
    const valueFragment = displayValue ? ` "${displayValue}"` : '';
    return [{ key, keys: [key], label: `${colLabel}: ${opLabel}${valueFragment}` }];
  }));

  if (chips.length === 0) return null;

  return (
    <div className={styles.bar} role="group" aria-label={i18n.t('filters.active')}>
      {chips.map((chip) => (
        <Button
          key={chip.key}
          size="small"
          shape="circular"
          appearance="secondary"
          icon={<Dismiss12Regular />}
          iconPosition="after"
          aria-label={i18n.t('filters.removeChip', { chip: chip.label })}
          onClick={() => {
            const next = { ...value };
            for (const k of chip.keys) delete next[k];
            onChange(next);
          }}
        >
          {chip.label}
        </Button>
      ))}
    </div>
  );
}
